import { useState, useEffect } from 'react'
import { AdminSettingsService } from '../services/adminSettings'
import { AI_MODELS } from '../constants/aiModels'
import type { AdminSettingKey } from '../types/adminSettings'

interface CriteriaFields {
  scoring: string
  feedback: string
}

interface CriteriaState {
  school: CriteriaFields
  entre: CriteriaFields
  system: CriteriaFields
}

const KEYS = {
  school: {
    scoring: 'homework_school_scoring_criteria' as AdminSettingKey,
    feedback: 'homework_school_feedback_criteria' as AdminSettingKey,
  },
  entre: {
    scoring: 'homework_entre_scoring_criteria' as AdminSettingKey,
    feedback: 'homework_entre_feedback_criteria' as AdminSettingKey,
  },
  system: {
    scoring: 'homework_system_scoring_criteria' as AdminSettingKey,
    feedback: 'homework_system_feedback_criteria' as AdminSettingKey,
  },
}

const SECTIONS: { key: keyof CriteriaState; label: string }[] = [
  { key: 'school', label: '学校' },
  { key: 'entre', label: 'アントレ部' },
  { key: 'system', label: 'システム宿題' },
]

const emptyState = (): CriteriaState => ({
  school: { scoring: '', feedback: '' },
  entre: { scoring: '', feedback: '' },
  system: { scoring: '', feedback: '' },
})

const MODEL_KEY = 'homework_grading_ai_model' as AdminSettingKey
const MAX_TOKENS_KEY = 'homework_grading_max_tokens' as AdminSettingKey
const DEFAULT_MAX_TOKENS = 2000

const HomeworkGradingCriteriaManager = () => {
  const [criteria, setCriteria] = useState<CriteriaState>(emptyState())
  const [model, setModel] = useState('')
  const [maxTokens, setMaxTokens] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      const result = await AdminSettingsService.getSettingsByCategory('homework_grading')
      if (result.success) {
        const map: Record<string, string> = {}
        for (const s of result.data) {
          map[s.key] = typeof s.value === 'string' ? s.value : ''
        }
        const mt = result.data.find(s => s.key === 'homework_grading_max_tokens')?.value
        setMaxTokens(mt != null && mt !== '' ? String(mt) : '')
        setCriteria({
          school: {
            scoring: map['homework_school_scoring_criteria'] ?? '',
            feedback: map['homework_school_feedback_criteria'] ?? '',
          },
          entre: {
            scoring: map['homework_entre_scoring_criteria'] ?? '',
            feedback: map['homework_entre_feedback_criteria'] ?? '',
          },
          system: {
            scoring: map['homework_system_scoring_criteria'] ?? '',
            feedback: map['homework_system_feedback_criteria'] ?? '',
          },
        })
        setModel(map['homework_grading_ai_model'] ?? '')
      }
      setIsLoading(false)
    }
    load()
  }, [])

  const handleChange = (section: keyof CriteriaState, field: 'scoring' | 'feedback', value: string) => {
    setCriteria(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setMessage(null)

    const parsedMaxTokens = parseInt(maxTokens, 10)
    const maxTokensValue = !isNaN(parsedMaxTokens) && parsedMaxTokens > 0 ? parsedMaxTokens : DEFAULT_MAX_TOKENS

    const updates: Array<{ key: AdminSettingKey; value: string | number }> = [
      { key: KEYS.school.scoring, value: criteria.school.scoring },
      { key: KEYS.school.feedback, value: criteria.school.feedback },
      { key: KEYS.entre.scoring, value: criteria.entre.scoring },
      { key: KEYS.entre.feedback, value: criteria.entre.feedback },
      { key: KEYS.system.scoring, value: criteria.system.scoring },
      { key: KEYS.system.feedback, value: criteria.system.feedback },
      { key: MODEL_KEY, value: model },
      { key: MAX_TOKENS_KEY, value: maxTokensValue },
    ]

    // Try update first; if the row doesn't exist yet, create it
    const errors: string[] = []
    for (const u of updates) {
      const updateResult = await AdminSettingsService.updateSetting(u.key, u.value)
      if (!updateResult.success) {
        // Row may not exist — try creating it
        const createResult = await AdminSettingsService.createSetting(
          u.key, u.value, undefined, 'homework_grading', false
        )
        if (!createResult.success) {
          errors.push(u.key)
        }
      }
    }

    setIsSaving(false)
    if (errors.length === 0) {
      setMessage({ type: 'success', text: '保存しました' })
    } else {
      setMessage({ type: 'error', text: '一部の設定の保存に失敗しました' })
    }
    setTimeout(() => setMessage(null), 3000)
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">採点・システム基準</h2>
        <p className="mt-1 text-sm text-gray-500">
          各宿題タイプの採点基準とフィードバック基準を設定します。これらの基準はAIが採点を行う際に使用されます。
        </p>
      </div>

      <div className="mb-8 border border-gray-200 rounded-lg p-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          採点AIモデル
        </label>
        <p className="text-xs text-gray-500 mb-2">
          宿題採点で使用するAIモデルです。「デフォルト」を選ぶとシステム全体のデフォルトAIモデルが使用されます。
        </p>
        <select
          value={model}
          onChange={e => setModel(e.target.value)}
          className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">デフォルト (システム設定)</option>
          {AI_MODELS.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1 mt-5">
          最大出力トークン数
        </label>
        <p className="text-xs text-gray-500 mb-2">
          採点AIが生成する回答の最大トークン数です。未入力の場合は {DEFAULT_MAX_TOKENS} が使用されます。
        </p>
        <input
          type="number"
          value={maxTokens}
          onChange={e => setMaxTokens(e.target.value)}
          min={100}
          max={8000}
          step={100}
          placeholder={String(DEFAULT_MAX_TOKENS)}
          className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="space-y-8">
        {SECTIONS.map(({ key, label }) => (
          <div key={key} className="border border-gray-200 rounded-lg p-5">
            <h3 className="text-base font-medium text-gray-800 mb-4">{label}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}採点基準
                </label>
                <textarea
                  value={criteria[key].scoring}
                  onChange={e => handleChange(key, 'scoring', e.target.value)}
                  rows={5}
                  placeholder={`${label}宿題の採点基準を入力してください`}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}フィードバック基準
                </label>
                <textarea
                  value={criteria[key].feedback}
                  onChange={e => handleChange(key, 'feedback', e.target.value)}
                  rows={5}
                  placeholder={`${label}宿題のフィードバック基準を入力してください`}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-md text-sm font-medium"
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
        {message && (
          <span className={`text-sm font-medium ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  )
}

export default HomeworkGradingCriteriaManager
